/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import React, { useRef, useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { taskInspectionAndProgressFormUrl } from "@Services/dataManager";
import { deleteForms, archiveForms, formUserSettingsController } from "@Actions/Forms";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { formCategories, presetSectionsCodeObject, getInspectionUrl, FORM_LIST_GRID_TYPE } from "@Helpers";
import { errorCodes } from "@Helpers/errorCodes";
import InspectionCreation from "./InspectionCreation";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { formStatuses } from "./constants";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { DisabledIllustration } from "@Features/commercial-shared/contracts/contractHelpers";
import Badge from "@mui/material/Badge";
import useFromList from "./useFromList";
import Text from "@CubeComponents/html/Text/Text";
import SlickGrid1, { withDynamicColumns } from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Box } from "@mui/material";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { GridClickEvent, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import type { FormInstance } from "../../formInstance.wire";
import type {
  FormUserSettingsControllerResponse,
  TaskInspectionAndProgressFormsControllerResponse,
} from "@ServerTypes/forms/forms.schema";

/**
 * A row of the forms list, as `/forms/taskInspectionAndProgressFormsController` returns them.
 *
 * That response is `result: z.array(z.any())`, so there is no server row type to import. The fields
 * are therefore named here, with their types taken from `FormInstance` wherever the row carries a
 * whole instance sub-object (`space`, `selectedForm`, `lastAction`, …) so those cannot drift from the
 * stored shape. The remaining fields (`currentLevelName`, `contractOrgName`, …) are grid-only columns
 * the aggregation adds. SERVER SCHEMA GAP — reported.
 */
interface FormsListRow {
  _id: ObjectIdString;
  counter?: number;
  title?: string;
  description?: string;
  status?: string;
  resourceType?: string;
  projectId?: FormInstance["projectId"];
  contractId?: FormInstance["contractId"];
  space?: FormInstance["space"];
  selectedForm: FormInstance["selectedForm"];
  selectedFormFlow?: FormInstance["selectedFormFlow"];
  createdBy?: FormInstance["createdBy"];
  createdDate?: number;
  dueDate?: FormInstance["dueDate"];
  lastAction?: FormInstance["lastAction"];
  isArchived?: FormInstance["isArchived"];
  currentLevelName?: string;
  contractOrgName?: string;
  contractName?: string;
  contractType?: string;
}

/**
 * `deleteTaskInspectionAndProgress` answers with a `message` (everything deleted) or a
 * `warningMessage` (some rows skipped by permission), neither of which the shared
 * `formStatusResponse` envelope models. SERVER SCHEMA GAP — reported; read through this view type
 * so the two fields are declared once instead of asserted at every use.
 */
type DeleteFormsResponseData = TaskInspectionAndProgressFormsControllerResponse & {
  message?: string;
  warningMessage?: string;
};

/**
 * `formUserSettingsControllerResponse` is a bare `{ status, result, count }` envelope, but the
 * `createForm` action also answers with the new form's `id` and `contractId` — how the caller
 * navigates to it. SERVER SCHEMA GAP — reported; read through this view type meanwhile.
 */
type CreateFormResponseData = FormUserSettingsControllerResponse & {
  id?: string;
  contractId?: string;
};

/**
 * One column of the forms grid.
 *
 * Declared locally rather than via `typedColumns<FormsListRow>` because several columns address
 * nested values by dotted path (`"space.spaceName"`, `"lastAction.userName"`), which
 * `ColumnDefinition`'s `field: keyof T` cannot express. The array is branded at the prop by
 * `withDynamicColumns()` — SlickGrid1's documented mint for exactly that mix — so a TYPE ALIAS, not
 * an interface: only an alias gets the implicit index signature that makes each entry assignable to
 * the `Record<string, unknown>` arm of that function's parameter.
 */
type FormsGridColumn = {
  field: string;
  id?: string;
  name?: string;
  width?: number;
  minWidth?: number;
  filterType?: "Text" | "Number" | "Date";
  _valueGetter?: (row: FormsListRow) => unknown;
  formatter?: (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: FormsListRow) => unknown;
};

/**
 * A toolbar entry for this grid. `ToolbarItem` declares `text`, but `ToolBar.tsx` renders
 * `item.name` for these built-in buttons, so `name` is added rather than renamed — nothing about the
 * rendered toolbar changes.
 */
type FormsToolbarItem = ToolbarItem & { name?: string };

/** The slice of the SlickGrid1 imperative handle this grid drives. */
type FormsGridHandle = {
  slickGrid: {
    getSelectedRows: () => number[];
    setSelectedRows: (rows: number[]) => void;
    getDataItem: (row: number) => FormsListRow;
  };
  refresh: () => void;
  dataView: {
    getAllSelectedItems: () => FormsListRow[];
  };
};

/**
 * How many rows the grid is reporting as selected.
 *
 * SlickGrid1 declares `onSelectedRowIdsChanged` as taking slickgrid's opaque `SlickEventArgs` (an
 * index-signature bag) while what it dispatches is a CustomEvent — its own comment calls that a
 * KNOWN MISMATCH and notes that re-declaring the prop would re-check 117 consumers. The selected row
 * indices really are on it: SlickDataView puts `rows: selectedRows` on every
 * `onSelectedRowIdsChanged` notify (@slickgrid-universal/common/dist/esm/core/slickDataview.js).
 * Read through this narrowing rather than asserted onto a hand-written event shape.
 */
const selectedRowCount = (args: Record<string, unknown>): number => {
  const detail: unknown = args.detail;
  if (typeof detail !== "object" || detail === null || !("args" in detail)) return 0;
  const eventArgs: unknown = detail.args;
  if (typeof eventArgs !== "object" || eventArgs === null || !("rows" in eventArgs)) return 0;
  const rows: unknown = eventArgs.rows;
  return Array.isArray(rows) ? rows.length : 0;
};

/**
 * The list query this grid drives SlickGrid1 with, before any caller override. `projectId` is
 * optional because it is the route param, and the portal "Take action" mount has none — that mount
 * always supplies its own `customQuery`, so the field simply never reaches the wire empty.
 */
interface FormsListQuery {
  projectId?: ObjectIdString;
  resourceType: string;
  contractId?: string;
  contractVersionId?: string;
  params: {
    projectId?: ObjectIdString;
    resourceType: string;
    contractId?: string;
    contractVersionId?: string;
  };
  where: Array<Record<string, unknown>>;
}

interface FormsProps {
  formCategory: string;
  source?: string;
  showOnlyApproved?: boolean;
  customQuery?: object;
  showOnlyGrid?: boolean;
  allowRecordClick?: boolean;
  showNavigateColumn?: boolean;
  setIsRowSelected?: (selected: boolean) => void;
  // setCount?: any;
}
const TasksForms = forwardRef(
  (
    {
      formCategory,
      source = "",
      showOnlyApproved,
      customQuery,
      showOnlyGrid = false,
      allowRecordClick = true,
      showNavigateColumn = false,
      setIsRowSelected,
      // setCount,
    }: FormsProps,
    ref,
  ) => {
    const { t } = useTranslation();
    // Typed through react-router's own generic rather than asserted: every route param really is
    // possibly-absent here — the portal "Take action" screen mounts this grid outside any project
    // route, which is why the views options below are gated on `Boolean(params.projectId)`. The id
    // itself is not validated client-side; the backend route schema does that and 400s if malformed.
    const params = useParams<{ projectId: ObjectIdString; contractId: string }>();
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.userDetails);
    const contractDetails = useSelector((state: RootState) => state.contractDetails);
    const [showFormFlowCreationModal, setShowFormFlowCreationModal] = useState({
      status: false,
      category: formCategory,
    });
    const [showArchivedForms, setShowArchivedForms] = useState(false);

    const [toolBarBtnState, setToolBarBtnState] = useState({
      delete: false,
      archive: false,
      unArchive: false,
    });

    const {
      userSettingsExists,
      resetConfig,
      showWarning,
      showWarningModal,
      hideWarningModal,
      resetConfigClick,
      moduleTxt,
      // Types flow from `useFromList`'s own return rather than being restated here.
    } = useFromList({
      projectId: params.projectId,
      formCategory: formCategory,
      showOnlyGrid,
    });

    const tempRef = useRef<FormsGridHandle | null>(null);
    // `forwardRef` is deliberately left without a type parameter here, so `ref` arrives as
    // `ForwardedRef<unknown>`. Declaring it (`forwardRef<FormsGridHandle, FormsProps>`) is the real
    // fix and would make this assertion disappear, but linkedForm.tsx attaches refs declared with
    // its OWN, structurally different `FormsGridHandle` (`{ dataView: { getAllSelectedItems: () =>
    // Array<SelectedFormRow | undefined> } }`, linkedForm.tsx:34-36, attached at linkedForm.tsx:127
    // and :140) — narrowing this component's public ref type rejects that file's two `ref=` props,
    // so the two have to be reconciled into one exported handle type together.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref type is `unknown` because forwardRef carries no type parameter; see comment above (blocked on linkedForm.tsx:34)
    const dataGrid = (ref || tempRef) as React.RefObject<FormsGridHandle | null>;
    const dispatch = useDispatch();

    const isModuleDisabled = params.contractId && !contractDetails.modulesEnabled[formCategory];

    const initialQuery: FormsListQuery = {
      projectId: params.projectId,
      resourceType: formCategory,
      contractId: params.contractId,
      contractVersionId: contractDetails.versionId,
      params: {
        projectId: params.projectId,
        resourceType: formCategory,
        contractId: params.contractId,
        contractVersionId: contractDetails.versionId,
      },
      where: [
        {
          isComplex: false,
          field: "isArchived.status",
          operator: "equal",
          ignoreCase: false,
          value: showArchivedForms,
        },
      ],
    };

    if (source == "contract" && showOnlyApproved) {
      initialQuery.where = [
        {
          isComplex: true,
          ignoreCase: false,
          condition: "and",
          predicates: [
            {
              isComplex: false,
              field: "isArchived.status",
              operator: "equal",
              ignoreCase: false,
              value: showArchivedForms,
            },
            {
              isComplex: false,
              field: "isApproved",
              operator: "equal",
              ignoreCase: false,
              value: true,
            },
          ],
        },
      ];
    }

    const [query, setQuery] = useState(customQuery || initialQuery);

    useEffect(() => {
      const updatedQuery = customQuery || initialQuery;
      setQuery(updatedQuery);
    }, [showArchivedForms, formCategory, customQuery]);

    const progressTemplate = (args: FormsListRow, returnVal = false) => {
      let totalMandatoryFields = 0;
      let mandatoryFieldsFilled = 0;

      args.selectedForm.template.forEach((section) => {
        if (section.questionFields) {
          if (section.sectionCode != presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION) {
            section.questionFields.forEach((field) => {
              const answerValue = field.answer?.value;
              if (field.isMandatory) {
                totalMandatoryFields = totalMandatoryFields + 1;
                if (
                  !(
                    (["", undefined, null, [], 0] as unknown[]).includes(answerValue) ||
                    (Array.isArray(answerValue) && answerValue.join("").trim() == "")
                  )
                ) {
                  mandatoryFieldsFilled = mandatoryFieldsFilled + 1;
                }
              }
            });
          }
        }
      });
      const formCompletionPercent = Number(((mandatoryFieldsFilled * 100) / totalMandatoryFields).toFixed(2)) || 0;
      if (returnVal) {
        return formCompletionPercent;
      }
      return `${formCompletionPercent}%`;
    };

    const handleFormOpenClick = (e: React.MouseEvent, args: FormsListRow) => {
      e.stopPropagation();

      const location = getInspectionUrl({
        resourceType: args.resourceType,
        resourceId: args._id,
        projectId: args.projectId,
        contractId: args.contractId,
      });

      window.open(location, "_blank");
    };

    const navigateTemplate = (args: FormsListRow) => {
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="cursor-pointer">
          <IconComponent
            onClick={(e: React.MouseEvent) => handleFormOpenClick(e, args)}
            className="cursor-pointer"
            name="openInViewer"
            width="1.25rem"
            height="1.25rem"
          />
        </div>,
      );
      return container;
    };
    const statusTemplate = (args: FormsListRow) => {
      let status = "";
      let color;
      const stage = args.status;
      if (stage == formStatuses.inProgress) {
        status = "In progress";
        color = "bg-system_colour_12";
      }
      if (stage == formStatuses.submitted) {
        status = "Submitted";
        color = "bg-system_colour_45";
      }
      if (stage == formStatuses.approved) {
        status = "Approved";
        color = "bg-positive_communication";
      }
      if (stage == formStatuses.rejected) {
        status = "Rejected";
        color = "bg-system_colour_8";
      }
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-30 rounded-2xl pe-3 flex items-center`}>
          <span className={`${color} rounded-full h-3 w-3`} />
          <span className="font-semibold">{status}</span>
        </div>,
      );
      return container;
    };

    function formatCreatedDate(data: FormsListRow, type: "createdDate" | "dueDate" | "lastAction") {
      let date;
      if (type === "lastAction") {
        // KNOWN BUG, preserved verbatim: `lastAction` is `.optional()` on the wire but is dereferenced
        // unconditionally here, so a row that has never been acted on throws TypeError while the grid
        // formats this cell. Asserted (erased at emit) to keep the existing runtime; the real fix is a
        // guard plus an empty-state path — a behaviour change, out of scope for a typing pass.
        if (data.lastAction!.timeStamp == null) {
          return "-";
        }
        date = data.lastAction!.timeStamp;
      } else {
        date = data[type];
      }
      let showDueDateMarker = false;
      let content = untranslated("");
      let color = "";
      if (type == "dueDate") {
        const today = Date.now();
        const threeDaysFromNow = today + 259200000;
        // `date` is `data.lastAction!.timeStamp` or `data[type]`; the early `return "-"` above
        // covers the absent case, so every path reaching here has a timestamp. Erased.
        if (date! <= threeDaysFromNow) {
          showDueDateMarker = true;
          const days = ((date! - today) / (1000 * 60 * 60 * 24)).toFixed(2);
          content = t("Due date approaching in {{days}} days", { days });
          color = "#ffcf55";
        }
        if (date! <= today) {
          showDueDateMarker = true;
          const days = ((today - date!) / (1000 * 60 * 60 * 24)).toFixed(2);
          content = t("Due date exceeded by {{days}} days", { days });
          color = "#e75555";
        }
      }
      const dateFormat = convertDate(Number(date), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="flex items-center gap-3">
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(date), "dateForText")}</span>
          </ToolTipComponent>
          {showDueDateMarker && (
            <IconComponent
              showGradient
              color={color}
              name="details"
              width="1.5rem"
              height="1.5rem"
              position="top"
              toolTipContent={content}
            />
          )}
        </div>,
      );
      return container;
    }

    function createdByTemplate(params: FormsListRow, type: "createdBy" | "lastAction") {
      let _id;
      if (type === "lastAction") {
        // KNOWN BUG, preserved verbatim, as above: `lastAction` is wire-optional and `createdBy._id`
        // is likewise `.optional()`, yet both are read unconditionally here. Asserted (erased at emit).
        if (params.lastAction!.userId == "") {
          return "-";
        }
        _id = params.lastAction!.userId;
      } else {
        _id = params.createdBy!._id;
      }

      const container = document.createElement("div");
      // @ts-expect-error -- a `string | null | undefined` tooltip/marker value reaches a slot that requires a value; the row field is optional on the form schema.
      renderUserTag(container, { id: _id });
      return container;
    }

    const columnDefinitions = useRef<FormsGridColumn[]>([
      ...(showNavigateColumn
        ? [
            {
              field: "_id",
              name: "",
              minWidth: 100,
              // Inside a conditional spread, so the `useRef<FormsGridColumn[]>` annotation does not
              // reach this literal and the parameters get no contextual type. Only the 5th (the
              // row) is read; `navigateTemplate` takes a `FormsListRow`.
              formatter: (_data: unknown, _params: unknown, _a: unknown, _b: unknown, value: FormsListRow) =>
                navigateTemplate(value),
            },
          ]
        : []),
      {
        field: "counter",
        id: "counter",
        name: t("Form No."),
        filterType: "Number",
        minWidth: 100,
      },
      {
        field: "title",
        id: "title",
        filterType: "Text",
        name: t("Title"),
        minWidth: 150,
      },
      {
        field: "description",
        id: "description",
        filterType: "Text",
        name: t("Description"),
        minWidth: 120,
      },
      {
        field: "space.spaceName",
        id: "spaceName",
        filterType: "Text",
        name: t("Space Name"),
        minWidth: 150,
        _valueGetter: (row) => row.space?.spaceName,
        // `space` and `lastAction` are `.optional()` on the wire but the formatters below read them
        // unconditionally, exactly as they do today. Asserted (erased at emit).
        formatter: (_data, _params, _a, _b, value) => value.space!.spaceName,
      },
      {
        field: "status",
        id: "status",
        name: t("Status"),
        width: 150,
        filterType: "Text",
        minWidth: 150,
        _valueGetter: (row) => row.status,
        formatter: (_data, _params, _a, _b, value) => statusTemplate(value),
      },
      {
        field: "selectedForm.name",
        id: "selectedForm.name",
        name: t("Template Attached"),
        minWidth: 150,
        filterType: "Text",
        // Declared one step WIDER than `FormsGridColumn._valueGetter` (a getter may accept a wider
        // parameter than the slot it fills), because a value-getter is not only handed rows: the
        // grouping pass runs every dataset item through it (SlickGrid1/groupUtils.ts:130) and the
        // excel export maps `dataView.getItems()` without filtering (SlickGrid1/index.tsx:1220), and
        // that dataset also holds SlickGrid1's own synthesised filler rows — `{ __isDummy: true }`
        // plus an id, carrying none of a form's fields (SlickGrid1/index.tsx:1887,
        // slickGridUtils.ts:379; the FORMATTER paths guard them at index.tsx:2306/2345/2378 and
        // ToolBar.tsx:536/964, this one does not). `selectedForm` is required on a real row
        // (InspectionAndProgress, cube-qa: 1483/1483 carry it), so the guard is about the filler.
        _valueGetter: (row: Partial<FormsListRow>) => row.selectedForm?.name,
      },
      {
        field: "selectedForm.type.name",
        id: "selectedForm.type.name",
        filterType: "Text",
        name: t("Attached Template Type"),
        minWidth: 180,
      },
      {
        field: "selectedFormFlow.name",
        id: "selectedFormFlow.name",
        name: t("Form Flow"),
        minWidth: 120,
        filterType: "Text",
      },
      {
        field: "createdBy.fullName",
        id: "createdBy.fullName",
        name: t("Raised by"),
        minWidth: 150,
        filterType: "Text",
        _valueGetter: (row) => row.createdBy?.fullName,
        formatter: (_data, _params, _a, _b, value) => createdByTemplate(value, "createdBy"),
      },
      {
        field: "createdDate",
        id: "createdDate",
        name: t("Raised On"),
        minWidth: 120,
        filterType: "Date",
        formatter: (_data, _params, _a, _b, value) => formatCreatedDate(value, "createdDate"),
      },
      {
        field: "dueDate",
        id: "dueDate",
        name: t("Due Date"),
        minWidth: 200,
        filterType: "Date",
        formatter: (_data, _params, _a, _b, value) => (value.dueDate ? formatCreatedDate(value, "dueDate") : "-"),
      },
      {
        field: "formProgress",
        id: "formProgress",
        name: t("Form Progress"),
        minWidth: 150,
        _valueGetter: (row) => progressTemplate(row, true),
        formatter: (_data, _params, _a, _b, value) => progressTemplate(value),
      },
      {
        field: "currentLevelName",
        id: "currentLevelName",
        name: t("Current Level"),
        filterType: "Text",
        minWidth: 150,
      },
      {
        field: "lastAction.userName",
        id: "lastAction.userName",
        name: t("Last Action By"),
        minWidth: 150,
        filterType: "Text",
        formatter: (_data, _params, _a, _b, value) => createdByTemplate(value, "lastAction"),
      },
      {
        field: "lastAction.actionName",
        id: "lastAction.actionName",
        name: t("Last Action"),
        minWidth: 120,
        filterType: "Text",
        formatter: (_data, _params, _a, _b, value) => value.lastAction!.actionName,
      },
      {
        field: "lastAction.timeStamp",
        id: "lastAction.timeStamp",
        name: t("Last Action Time"),
        minWidth: 150,
        formatter: (_data, _params, _a, _b, value) => formatCreatedDate(value, "lastAction"),
      },
      {
        field: "contractOrgName",
        id: "contractOrgName",
        name: t("Org Name"),
        minWidth: 150,
      },
      {
        field: "contractName",
        id: "contractName",
        name: t("Contract Name"),
        minWidth: 150,
      },
      {
        field: "contractType",
        id: "contractType",
        name: t("Contract Type"),
        minWidth: 150,
      },
    ]);

    const handleChange = () => {
      setShowArchivedForms((prev) => !prev);
    };

    const dropDownTemplate = () => (
      <SelectDropDown
        select={handleChange}
        cssClass="e-caret-hide"
        bgColor="transparent"
        items={
          showArchivedForms
            ? [{ text: t("Show Active Forms"), id: "showArchive" }]
            : [{ text: t("Show Archived Forms"), id: "showActive" }]
        }
      >
        <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
      </SelectDropDown>
    );

    const archivedChipTemplate = () => (
      <span className="rounded-2xl ps-4 pe-4 pt-1 pb-1 text-sm bg-text_light text-white">
        <Text>{t("Archived Forms")}</Text>
      </span>
    );

    const formConfigTemplate = () => (
      <Box
        onClick={() => toolbarClickHandler("reset_form_config")}
        className="flex cursor-pointer items-center pe-3 gap-2"
      >
        <IconComponent name="rollbackReviewIcon" width="1rem" height="1rem" />
        <Text>{t("Reset Form Configuration")}</Text>
        {userSettingsExists && (
          <ToolTipComponent
            showTipPointer={true}
            position={"top"}
            content={t(
              "User settings exists, Meaning form will directly be created on clicking of create button skipping all form creation steps. To reset click here ",
            )}
          >
            <Badge color="primary" variant="dot" />
          </ToolTipComponent>
        )}
      </Box>
    );

    const toolbarOptions: FormsToolbarItem[] = [
      ...(formCategory != formCategories.changeOrder && (source == "project" || params.contractId) && !showArchivedForms
        ? [
            {
              name: t("Create"),
              prefixIcon: "add",
              id: "custom_create",
            },
          ]
        : []),
      ...(!showArchivedForms
        ? [
            {
              name: t("Archive"),
              prefixIcon: "archive",
              id: "custom_archive",
              disabled: !toolBarBtnState.archive,
            },
            {
              name: t("Delete"),
              prefixIcon: "delete",
              id: "custom_delete",
              disabled: !toolBarBtnState.delete,
            },
          ]
        : []),
      ...(showArchivedForms
        ? [
            {
              name: t("Un-Archive"),
              prefixIcon: "unarchive",
              id: "custom_unArchive",
              disabled: !toolBarBtnState.unArchive,
            },
          ]
        : []),
      ...(showArchivedForms
        ? [
            {
              template: archivedChipTemplate,
              align: "Left" as const,
            },
          ]
        : []),
      ...(formCategory != formCategories.changeOrder
        ? [
            {
              name: t("Reset Form Configuration"),
              template: formConfigTemplate,
              id: "reset_form_config",
              rightAllign: true,
            },
          ]
        : []),
      {
        template: dropDownTemplate,
        rightAllign: true,
      },
    ];

    const handleDeleteForms = async () => {
      // Selection is read off the dataView rather than mapping `getSelectedRows` through `getDataItem`:
      // the row indices only cover the page SlickGrid currently has materialised, so a selection made
      // before scrolling was silently truncated. Empty-array fallback because the ref is unattached
      // until the grid mounts.
      const selectedItem = dataGrid.current?.dataView.getAllSelectedItems() || [];
      const resourceIds = selectedItem.map((data) => data._id);
      const res = await deleteForms({
        action: "delete",
        resourceIds,
        resourceType: formCategory,
      });
      if (res.data.status) {
        const responseData: DeleteFormsResponseData = res.data;
        hideWarningModal();
        dispatch(
          showToastNotification({
            content: untranslated(
              responseData.message ??
                responseData.warningMessage ??
                `Selected ${moduleTxt} have been deleted successfully`,
            ),
            type: responseData.warningMessage ? TOAST_TYPE_WARNING : TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current?.refresh();
      } else {
        hideWarningModal();
        dispatch(
          showToastNotification({
            // `errorMessage` is a server string, not a translated key, so it goes through the
            // `untranslated()` boundary helper the toast slice documents. It is `.optional()` on the
            // failure branch of `statusResponse`; an absent one keeps today's empty toast, because
            // the slice drops any payload whose `content` is falsy.
            content: untranslated(res.data.errorMessage ?? ""),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    };

    const changeFormState = async (state: "custom_archive" | "custom_unArchive") => {
      let status;
      let message;
      if (state == "custom_archive") {
        status = true;
        message = "Archived";
      } else {
        status = false;
        message = "Un-Archived";
      }
      // Same dataView read as the delete path: `getSelectedRows` only covers the rows SlickGrid has
      // materialised, so archiving a selection made before scrolling used to silently skip the rest.
      const allselectedRows = dataGrid.current?.dataView.getAllSelectedItems() || [];

      const allIds = allselectedRows.map((data) => data._id);
      const res = await archiveForms({
        action: "archive",
        resourceIds: allIds,
        data: { isArchived: { status } },
      });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Selected form(s) successfully {{message}}", { message: message }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current?.refresh();
        return;
      }
      // A partial run archived everything the caller could modify, so the grid is refreshed for it too
      // and the toast is a warning rather than a hard error; only the fully-denied case leaves the grid
      // untouched. The two are told apart by `errorCode` alone — the server sets
      // FORM_ARCHIVE_PARTIAL_PERMISSION on the first and INSUFFICIENT_PERMISSION on the second.
      const isPartial = res.data.errorCode === errorCodes.FORM_ARCHIVE_PARTIAL_PERMISSION;
      dispatch(
        showToastNotification({
          content: isPartial
            ? t("Some form(s) were skipped because you do not have permission to modify them")
            : t("You do not have permission to perform this action."),
          type: isPartial ? TOAST_TYPE_WARNING : TOAST_TYPE_ERROR,
        }),
      );
      if (isPartial) dataGrid.current?.refresh();
    };

    const toolbarClickHandler = async (args: string) => {
      if (args === "custom_delete") {
        showWarningModal("delete");
      }
      if (args === "custom_create") {
        // `userSettingsExists` is only ever set by `useFromList`'s check call, which that hook fires
        // solely when it HAS a `projectId` — so quick-create is already unreachable without the
        // route param. Reading it through this narrowing keeps the request's required `projectId`
        // honest without changing which clicks take this branch.
        const projectId = params.projectId;
        if (userSettingsExists && projectId) {
          const res = await formUserSettingsController({
            action: "createForm",
            projectId,
            resourceType: formCategory,
          });
          if (res.data.status) {
            const created: CreateFormResponseData = res.data;
            const location = getInspectionUrl({
              resourceId: created.id,
              projectId,
              resourceType: formCategory,
              contractId: created.contractId,
            });
            navigate(location);
            dispatch(
              showToastNotification({
                content: t("Form created successfully"),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          } else {
            dispatch(
              showToastNotification({
                // See the note on the other `errorMessage` toast above: server string, branded
                // through `untranslated()`, absent one keeps today's empty toast.
                content: untranslated(res.data.errorMessage ?? ""),
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        } else {
          setShowFormFlowCreationModal({
            status: true,
            category: formCategory,
          });
        }
      }
      if (args === "custom_archive") {
        changeFormState("custom_archive");
      }
      if (args === "custom_unArchive") {
        changeFormState("custom_unArchive");
      }
      if (args === "reset_form_config") {
        resetConfigClick();
      }
    };

    const getInspectionFormFLow = () => (
      <InspectionCreation
        projectId={params.projectId}
        showFormFlowCreationModal={showFormFlowCreationModal}
        setShowFormFlowCreationModal={setShowFormFlowCreationModal}
        source={params.contractId ? "contract" : "project"}
        spaceId={params.contractId ? contractDetails.space._id : null}
      />
    );

    const selectionEvent = (args: Record<string, unknown>) => {
      if (!dataGrid.current) return;

      const totalRowsSelected = selectedRowCount(args);
      if (setIsRowSelected) {
        setIsRowSelected(totalRowsSelected > 0);
      }
      if (showOnlyGrid) {
        return;
      }
      if (totalRowsSelected == 0) {
        setToolBarBtnState({
          delete: false,
          archive: false,
          unArchive: false,
        });
      }
      if (totalRowsSelected >= 1) {
        const btnState = {
          delete: true,
          archive: true,
          unArchive: true,
        };
        // `getAllSelectedItems()` is DECLARED to return `FormsListRow[]`, but SlickDataView builds
        // the array by pushing `getItemById(id)` for every id still in `selectedRowIds` —
        // `undefined` for a selection kept across a refresh/filter, and group rows have no `_id`
        // when grouping is on. Widened on assignment so the per-record guard below stays meaningful.
        const allselectedRows: Array<FormsListRow | undefined> = dataGrid.current.dataView.getAllSelectedItems();
        allselectedRows.forEach((record) => {
          if (record?.createdBy?._id != userDetails.id) {
            btnState.delete = false;
          }
        });
        setToolBarBtnState({
          ...btnState,
        });
      }
    };

    const recordClick = (args: GridClickEvent) => {
      if (showOnlyGrid) {
        return;
      }
      const rowIndex = args.detail.args.row;
      const clickedRow = dataGrid.current!.slickGrid.getDataItem(rowIndex);

      if (args.detail.args.cell != 0 && !clickedRow.isArchived?.status && source !== "forms" && allowRecordClick) {
        const location = getInspectionUrl({
          resourceType: clickedRow.resourceType,
          resourceId: clickedRow._id,
          projectId: clickedRow.projectId,
          contractId: clickedRow.contractId,
        });
        navigate(location);
      }
    };

    const getGridComponent = () => (
      <SlickGrid1
        key={formCategory}
        /* SlickGrid1's ref prop is `React.Ref<SlickgridReactInstanceX<FormsListRow>>` — the WHOLE
           grid instance — while this component declares only the slice it drives. The fix is to type
           this ref (and linkedForm.tsx's) as the exported `SlickGrid1Ref<FormsListRow>`, which is a
           cross-file change; see the note on `dataGrid` above. */
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- FormsGridHandle is a hand-declared slice of SlickgridReactInstanceX; see comment above
        ref={dataGrid as unknown as Parameters<typeof SlickGrid1<FormsListRow>>[0]["ref"]}
        remoteDataSource={taskInspectionAndProgressFormUrl}
        /* `FormsProps.customQuery` is declared `object`, so `query` is `object | FormsListQuery` and
           `object` cannot reach SlickGrid1's `QueryShape | { [key: string]: unknown }`. Re-typing
           `customQuery` to `QueryShape` requires useLinkedForm.ts's `interface FormsQuery` (line 86)
           to become a type alias first — an interface gets no implicit index signature, so it is not
           assignable to an index-signature target (TS2322). */
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- customQuery is typed `object`; see comment above (blocked on useLinkedForm.ts:86)
        query={query as Parameters<typeof SlickGrid1<FormsListRow>>[0]["query"]}
        toolbar={showOnlyGrid ? [] : toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={selectionEvent}
        infiniteScroll
        onClick={recordClick}
        enableSortingOption
        /* `ValidatedColumnDefinition`'s `__brand` is a phantom `unique symbol`, so no runtime value
           can carry it — only SlickGrid1's own `brandColumns()` mints it. `typedColumns()` cannot be
           used because seven of these columns address nested values by dotted path
           ("space.spaceName", "lastAction.userName", "selectedForm.type.name", …), which
           `ColumnDefinition["field"]: keyof T` cannot express. `withDynamicColumns()` is the mint
           SlickGrid1 documents for exactly that mix, and it returns the same array it is given. */
        columnDefinitions={withDynamicColumns<FormsListRow>(columnDefinitions.current)}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        enableViews={!showOnlyGrid && Boolean(params.projectId)}
        {...(!showOnlyGrid &&
          Boolean(params.projectId) && {
            viewsOptions: {
              headerName: "Views",
              name: "View",
              resourceType: formCategory,
              gridType: FORM_LIST_GRID_TYPE,
              selectedLayoutKey: `${formCategory}_SELECTED_LAYOUT`,
              fetchParams: { projectId: params.projectId },
            },
          })}
      />
    );
    const warningModal = () => {
      const source = showWarning.source;
      let btnText;
      let contentTxt;
      let headerText;
      let callBackFun;
      switch (source) {
        case "delete":
          btnText = "Delete";
          contentTxt = `${
            formCategory == formCategories.changeOrder ? "All Associated changes in the contract will also be lost" : ""
          } Are you sure you want to delete all selected ${moduleTxt} ?`;
          headerText = t("Delete {{module}}", { module: moduleTxt });
          callBackFun = handleDeleteForms;
          break;
        case "resetConfig":
          btnText = "Reset";
          contentTxt = "Are you sure you want to reset the saved configuration.?";
          headerText = t("Reset Configuration");
          callBackFun = resetConfig;
          break;
      }
      return (
        <ModalStateDialogComponent
          footerContent={
            <div className="flex gap-5 items-center">
              <ButtonComponent onClick={callBackFun} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                {btnText}
              </ButtonComponent>
              <ButtonComponent onClick={hideWarningModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          width="30%"
          headerTitle={headerText}
          onCancel={hideWarningModal}
          isVisible={showWarning.status}
          modelContent={<div className="">{contentTxt} </div>}
        />
      );
    };

    return (
      <div className="h-full w-full overflow-auto flex">
        {showWarning.status && warningModal()}
        {showFormFlowCreationModal.status && getInspectionFormFLow()}
        {isModuleDisabled ? <DisabledIllustration /> : getGridComponent()}
      </div>
    );
  },
);
TasksForms.displayName = "TasksForms";
export default TasksForms;
